/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {Fragment, memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useKeyboard} from '../../../../hooks/keyboard';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useSelector} from 'react-redux';
import {DeviceTypes} from "../../../../constants/DeviceTypes";

import {
    AlarmControlParameters, BRIDGE_DEVICE_TYPE_ID_ST, BRIDGE_DEVICE_TYPE_ID_STPM, BRIDGE_DEVICE_TYPE_ID_STS_PLUS,
    CONTROL_TYPE_ID_ALARM, CONTROL_TYPE_ID_ANALOG,
    CONTROL_TYPE_ID_DIMMER, CONTROL_TYPE_ID_ELEVATOR, CONTROL_TYPE_ID_LIGHT,
    CONTROL_TYPE_ID_SIREN,
    CONTROL_TYPE_ID_THERMOSTAT,
    ControlModel,
    ControlParameters,
    ControlRenderType,
    ControlTypeModel,
    DeviceParameters,
    DimmerControlParameters,
    INPUT_TYPE_ID_MOMENTARY,
    INPUT_TYPE_ID_SENSOR,
    IOControlParameters,
    PulseControlParameters,
    SimpleControlParameters,
    SirenControlParameters,
    TemperatureControlSettings,
    ThreePhaseControlParameters,
} from '../../../../types/control';
import {SelectItem} from '../../../../components/Select';
import {BridgeDeviceModel} from '../../../../types/bridge-device';
import {SettingCheckBoxComponent} from '../../../../components/Settings/SettingCheckboxComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {AreaModel} from '../../../../store/control/state';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {useSetSelector} from '../../../../hooks/set-selector';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {
    isValidDuration,
    isValidMaxDimmerValue,
    isValidMinDimmerValue,
    isValidPulseDuration,
} from '../../../../utils/validation';

import {CONTROL_WRAPPER, useControlSelector} from '../../../../hooks/control-selector';
import {useGlassControl} from '../../../../hooks/glass-control';
import {useMap} from '../../../../hooks/map';
import {SettingsDeleteButton} from '../../../../components/Settings/SettingsDeleteButton';
import {useLocalization} from '../../../../hooks/localization';

const BACK_URL = '/settings/control';
const INPUT_EDIT_URL = '/settings/input/edit';

const CONTROL_TYPE_VALUE_MAP: Record<ControlRenderType, SelectItem<number>[]> = {
    complex: [
        {
            value: 0,
            label: 'close_stop',
        },
        {
            value: 1,
            label: 'open_up',
        },
        {
            value: 2,
            label: 'down',
        },
        {
            value: 3,
            label: 'open_close',
        },
    ],
    simple: [
        {
            value: 0,
            label: 'close',
        },
        {
            value: 1,
            label: 'open',
        },
    ],
    three_phase: [
        {
            value: 0,
            label: 'stop',
        },
        {
            value: 1,
            label: 'up',
        },
        {
            value: 2,
            label: 'down',
        },
    ],
    two_phase: [
        {
            value: 1,
            label: 'up',
        },
        {
            value: 2,
            label: 'down',
        },
    ],
    pulse: [
        {
            value: 0,
            label: 'passive',
        },
        {
            value: 1,
            label: 'active',
        },
    ],
};

export const NO_OUTPUT_OPTION: SelectItem<number> = {value: 99, label: 'do_not_output'};

type ALARM_CONTROL_VALUE = {
    id: string;
    isVirtual: boolean;
};

export const ControlEditScreen = memo(function ControlEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();
    const location = useLocation<ControlModel>();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] =
        useKeyboard('numpad-extended', true);

    const controlList = useControlSelector();
    const controlSet = useSetSelector(state => state.control.controlList);
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList);
    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);
    const [optionControl, setOptionControl] = useState(false);

    const bridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] =
        useMemo(() => bridgeDeviceList
            .filter(bridgeDevice => (bridgeDeviceTypeSet[bridgeDevice.type_id]?.output_count || 0) > 0)
            .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
            .map(bridgeDevice => ({
                value: bridgeDevice,
                label: bridgeDevice.name,
            })), [bridgeDeviceList, bridgeDeviceTypeSet]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => location.state || controlSet[params?.id] || null, []);
    const [bridgeDevice, setBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        (selected == null ? null :
            bridgeDeviceOptionList.find(bridgeDeviceOption => bridgeDeviceOption.value.id === selected.bridge_device_id)
            || null),
    );
    const bridgeDeviceType = useMemo(() => bridgeDevice == null ? null : bridgeDeviceTypeSet[bridgeDevice.value.type_id],
        [bridgeDevice, bridgeDeviceTypeSet]);

    const bridgeDeviceDimmerCount = useMemo(() => bridgeDeviceType?.dimmer_count || 0, [bridgeDeviceType]);
    const isBridgeDeviceTypeIO = useMemo(() => bridgeDeviceType?.is_io_device || false, [bridgeDeviceType]);
    const usedOutputList = useMemo(() => {
        const list = [];
        for (const control of Object.values(controlSet)) {
            if (control.bridge_device_id !== bridgeDevice?.value.id) {
                continue;
            }

            list.push(control.parameters.output_number);

            if (controlTypeSet[control.type_id].render_type === 'three_phase' || controlTypeSet[control.type_id].render_type === 'two_phase') {
                list.push((control.parameters as ThreePhaseControlParameters).down_output_number);
            }
        }

        return list;
    }, [bridgeDevice, controlSet, controlTypeSet]);

    const alarmControlOptionList: SelectItem<CONTROL_WRAPPER>[] = useMemo(() => {
            const controls = controlList
                .filter(control => control.bridgeDeviceId === bridgeDevice?.value?.id && control.control.type_id !== CONTROL_TYPE_ID_ALARM && control.control.type_id !== CONTROL_TYPE_ID_SIREN)
                .map(control => ({label: control.control.name, value: control}));

            controls.unshift({
                label: t('do_not_output'), value: {
                    id: undefined,
                    isVirtual: false,
                    bridgeDeviceId: '',
                    glassControlParameters: undefined,
                    control: undefined,
                },
            });

            return controls;
        },
        [t, controlList, bridgeDevice?.value]);
    const outputNumberOptionList: SelectItem<number>[] = useMemo(() => {
        const outputCount = bridgeDeviceType?.output_count || 0;
        if (outputCount === 0) {
            return [];
        }

        const list = [];
        list.push({...NO_OUTPUT_OPTION, label: t(NO_OUTPUT_OPTION.label)});
        for (let i = 0; i < outputCount; i++) {
            //@ts-ignore
            const isUsedOutput = usedOutputList.some((o) => o === i && (o !== selected?.parameters.output_number && o !== selected?.parameters.down_output_number));
            list.push({value: i, label: `${t('output')} ${i.toString()}${isUsedOutput ? '*' : ''}`});
        }

        return list;
    }, [t, bridgeDeviceType, usedOutputList, selected?.parameters]);
    const doubledOutputNumberOptionList: SelectItem<number>[] = useMemo(() => {
        const outputCount = bridgeDeviceType?.output_count || 0;
        if (outputCount === 0) {
            return [];
        }

        const list = [];
        list.push({...NO_OUTPUT_OPTION, label: t(NO_OUTPUT_OPTION.label)});
        for (let i = 0; i + 1 < outputCount; i += 2) {
            //@ts-ignore
            const isUsedOutput = usedOutputList.some((o) => o === i && (o !== selected?.parameters.output_number && o !== selected?.parameters.down_output_number));
            list.push({
                value: i,
                label: `${t('output')} ${i.toString()} - ${(i + 1).toString()}${isUsedOutput ? '*' : ''}`,
            });
        }

        return list;
    }, [t, bridgeDeviceType, usedOutputList, selected?.parameters]);

    const controlTypeList = useSelector(state => state.control.controlTypeList);
    const controlTypeOptionList: SelectItem<Omit<ControlTypeModel, 'image_set'>>[] =
        useMemo(() => controlTypeList
            .filter(controlType =>
                controlType.allowed_bridge_device_type_id_list.indexOf(bridgeDevice?.value?.type_id) !== -1)
            .filter(controlType =>
                controlType.id !== CONTROL_TYPE_ID_DIMMER || bridgeDeviceDimmerCount > 0)
            .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
            .map(controlType => ({
                value: controlType,
                label: controlType.name,
            })), [bridgeDeviceDimmerCount, bridgeDevice?.value?.type_id, controlTypeList]);

    const [name, setName] = useState(selected?.id != null ? selected?.name : '');
    const [controlType, setControlType] = useState<SelectItem<Omit<ControlTypeModel, 'image_set'>>>(
        (selected == null ? null : controlTypeOptionList.find(controlTypeOption => controlTypeOption.value.id === selected.type_id)
            || null),
    );
    const [outputReverse, setOutputReverse] = useState((selected?.parameters as SimpleControlParameters)?.should_output_reverse || false);
    const [rememberLastValue, setRememberLastValue] = useState((selected?.parameters as IOControlParameters)?.should_remember_last_value || false);
    const [navigateToSTPEditor, setNavigateToSTPEditor] = useState<boolean>();

    const thermostatBridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMap(
        useFilterSelector(
            state => state.control.bridgeDeviceList,
            bridgeDeviceItem =>
                bridgeDeviceTypeSet[bridgeDeviceItem.type_id]?.is_temperature_device === true &&
                bridgeDevice?.value != null &&
                bridgeDevice.value.gateway_id === bridgeDeviceItem.gateway_id &&
                controlList.find(c =>
                    c.isVirtual === false &&
                    (c.control.temperature_settings?.bridge_device_id === bridgeDeviceItem.id && bridgeDeviceItem.id !== selected?.temperature_settings?.bridge_device_id)) == null,
            [bridgeDeviceTypeSet, bridgeDevice?.value, controlList, selected],
        ),
        bridgeDevice => ({value: bridgeDevice, label: bridgeDevice.name}),
        [],
    );

    const [thermostatBridgeDevice, setThermostatBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        (selected == null ? null :
            thermostatBridgeDeviceOptionList.find(b => b.value.id === selected.temperature_settings?.bridge_device_id)
            || null),
    );

    const thermostatBridgeDeviceType = useMemo(() => thermostatBridgeDevice == null ? null : bridgeDeviceTypeSet[thermostatBridgeDevice.value.type_id],
        [thermostatBridgeDevice, bridgeDeviceTypeSet]);

    const isThermostatBridgeDeviceIO = useMemo(() => thermostatBridgeDeviceType == null || thermostatBridgeDeviceType?.is_io_device, [thermostatBridgeDeviceType]);

    const [addInput, setAddInput] = useState(false);

    const {
        buttonNumberOptionList,
        setButtonNumber,
        buttonNumber,
    } =
        useGlassControl(selected, bridgeDevice?.value?.id || null, bridgeDeviceType, controlType?.value?.id);

    const areaList = useFilterSelector(state => state.control.areaList, area => area.type === 'room' || area.type === 'place', []);
    const areaOptionList: SelectItem<AreaModel>[] =
        useMemo(() => areaList.map(area => ({value: area, label: area.name})), [areaList]);
    const [areaOption, setAreaOption] = useState<SelectItem<AreaModel>>(
        (selected == null ? null : areaOptionList.find(areaOption => areaOption.value.id === selected.area_id)
            || null),
    );
    const [outputNumber, setOutputNumber] = useState<SelectItem<number>>(
        (selected == null ||
        (!isBridgeDeviceTypeIO && (controlType?.value.render_type === 'three_phase' || controlType?.value.render_type === 'two_phase')) ? null :
            outputNumberOptionList.find(outputNumberOption => outputNumberOption.value === selected.parameters?.output_number)
            || null),
    );
    const [downOutputNumber, setDownOutputNumber] = useState<SelectItem<number>>(
        (selected == null ||
        (!isBridgeDeviceTypeIO && (controlType?.value.render_type === 'three_phase' || controlType?.value.render_type === 'two_phase')) ? null :
            outputNumberOptionList.find(outputNumberOption => outputNumberOption.value === (selected.parameters as ThreePhaseControlParameters)?.down_output_number)
            || null),
    );
    const [doubledOutputNumber, setDoubledOutputNumber] = useState<SelectItem<number>>(
        (selected == null || isBridgeDeviceTypeIO || (controlType?.value.render_type !== 'three_phase' && controlType?.value.render_type !== 'two_phase') ? null :
            doubledOutputNumberOptionList.find(outputNumberOption => outputNumberOption.value === selected.parameters?.output_number)
            || null),
    );
    const [outputDurationLimitSeconds, setOutputDurationLimitSeconds] = useState(
        (selected?.parameters as ThreePhaseControlParameters)?.output_time_limit?.toString()
        || '');
    const [pulseDuration, setPulseDuration] = useState(
        (((selected?.parameters as PulseControlParameters)?.pulse_time || 0) / 10).toString()
        || '');
    const [maxValue, setMaxValue] = useState((selected?.parameters as DimmerControlParameters)?.max_limit?.toString()
        || '100');
    const [minValue, setMinValue] = useState((selected?.parameters as DimmerControlParameters)?.min_limit?.toString()
        || '0');
    const ALARM_LEVEL_LIST = useMemo(() => [
        {
            value: 1,
            label: t('level_1'),
        },
        {
            value: 2,
            label: t('level_2'),
        },
        {
            value: 3,
            label: t('level_3'),
        },
    ], [t]);
    const [alarmLevelOption, setAlarmLevelOption] = useState<SelectItem<number>>(
        selected == null ? ALARM_LEVEL_LIST[0] : (
            ALARM_LEVEL_LIST.find(alarmLevelOption => alarmLevelOption.value === (selected.parameters as SirenControlParameters)?.alarm_level)
            || null),
    );
    const SIREN_LEVEL_LIST = useMemo<SelectItem<number>[]>(() => [
        {
            value: 0,
            label: t('all'),
        },
        {
            value: 1,
            label: t('level_1'),
        },
        {
            value: 2,
            label: t('level_2'),
        },
        {
            value: 3,
            label: t('level_3'),
        },
    ], [t]);
    const [sirenLevelOption, setSirenLevelOption] = useState<SelectItem<number>>(
        selected == null ? SIREN_LEVEL_LIST[0] : (
            SIREN_LEVEL_LIST.find(sirenLevelOption => sirenLevelOption.value === (selected.parameters as SirenControlParameters)?.alarm_level)
            || null),
    );
    const [alarmControlOption, setAlarmControlOption] = useState<SelectItem<ALARM_CONTROL_VALUE>>(
        selected == null ? null : (
            alarmControlOptionList.find(option => option.value.id === (selected?.parameters as AlarmControlParameters)?.relational_control_id)
        ) || null,
    );
    const SIREN_LIST = useMemo<SelectItem<number>[]>(() => [
        {
            value: 0,
            label: t('inactive'),
        },
        {
            value: 1,
            label: t('intermittent'),
        },
        {
            value: 2,
            label: t('continuous'),
        },
    ], [t]);
    const [alarmSirenOption, setAlarmSirenOption] = useState<SelectItem<number>>(
        selected == null ? SIREN_LIST[0] : (
            SIREN_LIST.find(sirenOption => sirenOption.value === (selected.parameters as AlarmControlParameters)?.siren_type)
            || null),
    );
    const HEATING_MODE_LIST = useMemo<SelectItem<number>[]>(() => [
        {
            value: 1,
            label: t('heating'),
        },
        {
            value: 2,
            label: t('cooling'),
        },
        {
            value: 3,
            label: t('heating_cooling'),
        },
    ], [t]);
    const [heatingModeOption, setHeatingModeOption] = useState<SelectItem<number>>(
        selected == null ? HEATING_MODE_LIST[0] : (
            HEATING_MODE_LIST.find(heatingOption => heatingOption.value === (
                selected.temperature_settings?.has_cooling && selected.temperature_settings?.has_heating ? 3 :
                    (selected.temperature_settings?.has_cooling ? 2 : 1)
            ))
            || null),
    );
    const [alarmDelaySeconds, setAlarmDelaySeconds] = useState(
        (selected?.parameters as AlarmControlParameters)?.delay_time?.toString()
        || '');
    const defaultValueOptionList = useMemo(() => {
        if (!controlType) {
            return [];
        }

        return CONTROL_TYPE_VALUE_MAP[controlType.value.render_type].map(value => ({...value, label: t(value.label)}));
    }, [t, controlType]);
    const [defaultValueOption, setDefaultValueOption] = useState<SelectItem<number>>(
        (selected == null ? null : defaultValueOptionList.find(defaultValueOption => defaultValueOption.value === (selected.parameters as IOControlParameters)?.default_value))
        || defaultValueOptionList[0] || null,
    );

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const goToInputEditScreenWithState = useCallback((state: unknown) => {
        history.push({pathname: INPUT_EDIT_URL, state});
    }, [history]);

    const goToStpEditorWithState = useCallback((state: unknown) => {
        //     if (controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT) {
        //         state['controlId'] = controlList.find(c => c.isVirtual === true && state['controlId'] === c.control.id)?.id || state['controlId'];
        //     }
        // todo above not working, as a result thermostat virtualControl's stpUIEntity page cannot be redirected
        history.push({pathname: `/settings/stp-editor/${bridgeDevice.value.id}/content/`, state});
    }, [bridgeDevice, history/*, controlType, controlList*/]);

    const onDeletePressed = useCallback(() => {
        return rpc.deleteControl(selected?.id)
            .then(() => {
                success(t('control_successfully_deleted'));
                onBackPressed();
            })
            .catch(failure);
    }, [t, failure, rpc, success, onBackPressed, selected?.id]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure(t('pls_enter_control_name'));

            return false;
        }

        if (bridgeDevice?.value == null) {
            failure(t('pls_select_device'));

            return false;
        }

        if (controlType == null) {
            failure(t('pls_select_control_type'));

            return false;
        }

        if (areaOption?.value == null) {
            failure(t('pls_select_area'));

            return false;
        }

        if (controlType.value.id === CONTROL_TYPE_ID_ALARM) {
            if (alarmControlOption?.value == null) {
                failure(t('pls_select_control'));

                return false;
            }

            if (alarmSirenOption?.value == null) {
                failure(t('pls_select_siren'));

                return false;
            }

            if (alarmLevelOption?.value == null) {
                failure(t('pls_select_alarm_level'));

                return false;
            }

            if (!isValidDuration(alarmDelaySeconds)) {
                failure(t('pls_enter_a_valid_delay_duration'));

                return false;
            }
        } else if (controlType.value.id === CONTROL_TYPE_ID_SIREN) {
            if (outputNumber?.value == null) {
                failure(t('pls_enter_output_no'));

                return false;
            }

            if (sirenLevelOption?.value == null) {
                failure(t('pls_select_alarm_level'));

                return false;
            }
        } else if (controlType.value.id === CONTROL_TYPE_ID_THERMOSTAT) {
            if (isBridgeDeviceTypeIO && thermostatBridgeDevice?.value == null) {
                failure(t('pls_select_sensor_device'));

                return false;
            }

            if (!isBridgeDeviceTypeIO) {
                const foundControl = controlList.find(c => c.isVirtual === false &&
                    c.control.id !== selected?.id &&
                    c.control.temperature_settings?.bridge_device_id === bridgeDevice.value.id,
                );
                if (foundControl != null) {
                    failure(t('thermostat_was_previously_added_to_device', {
                        deviceName: bridgeDevice.value.name,
                        controlName: foundControl.control.name,
                    }));

                    return false;
                }

            }

            if (!isBridgeDeviceTypeIO) {
                if (heatingModeOption?.value === 2 || heatingModeOption?.value === 3) {
                    if (downOutputNumber?.value == null) {
                        failure(t('pls_select_cold_exit_no'));

                        return false;
                    }
                }


                if (heatingModeOption?.value === 1 || heatingModeOption?.value === 3) {
                    if (outputNumber?.value == null) {
                        failure(t('pls_select_hot_exit_no'));

                        return false;
                    }
                }
            } else {
                if (outputNumber?.value == null) {
                    failure(t('pls_select_exit_no'));

                    return false;
                }
            }
        } else {
            if (isBridgeDeviceTypeIO) {
                switch (controlType.value.render_type) {
                    case 'simple':
                        if (outputNumber?.value == null) {
                            failure(t('pls_select_exit_no'));

                            return false;
                        }

                        if (!rememberLastValue && defaultValueOption?.value == null && bridgeDevice?.value?.type_id !== DeviceTypes.ETHERNET_IO) {
                            failure(t('pls_select_default_value'));

                            return false;
                        }
                        break;
                    case 'two_phase':
                    case 'three_phase':
                        if (outputNumber?.value == null) {
                            failure(t('pls_select_up_exit_no'));

                            return false;
                        }

                        if (downOutputNumber?.value == null) {
                            failure(t('pls_select_down_exit_no'));

                            return false;
                        }

                        if (outputNumber.value !== NO_OUTPUT_OPTION.value &&
                            outputNumber.value === downOutputNumber.value) {
                            failure(t('up_down_exit_no_cannot_be_same'));

                            return false;
                        }

                        if (!isValidDuration(outputDurationLimitSeconds)) {
                            failure(t('pls_enter_a_valid_exit_duration_limit'));

                            return false;
                        }

                        if (!rememberLastValue && defaultValueOption?.value == null && bridgeDevice?.value?.type_id !== DeviceTypes.ETHERNET_IO) {
                            failure(t('pls_select_default_value'));

                            return false;
                        }

                        break;
                    case 'pulse':
                        if (outputNumber?.value == null) {
                            failure(t('pls_select_up_exit_no'));

                            return false;
                        }

                        if (!isValidPulseDuration(pulseDuration)) {
                            failure(t('pls_enter_a_valid_pulse_duration'));

                            return false;
                        }

                        if (!rememberLastValue && defaultValueOption?.value == null && bridgeDevice?.value?.type_id !== DeviceTypes.ETHERNET_IO) {
                            failure(t('pls_select_default_value'));

                            return false;
                        }

                        break;
                    case 'complex':
                        if (!rememberLastValue && defaultValueOption?.value == null && bridgeDevice?.value?.type_id !== DeviceTypes.ETHERNET_IO) {
                            failure(t('pls_select_default_value'));

                            return false;
                        }

                        //failure(t('undefined_control_type'));
                        console.log("645")
                       break;
                    default:
                        break;
                }
            } else {
                if (bridgeDeviceType?.button_count > 0) {
                    if (buttonNumber?.value == null) {
                        failure(t('pls_select_button_number'));

                        return false;
                    }
                }

                switch (controlType.value.render_type) {
                    case 'simple':
                        if (outputNumber?.value == null) {
                            failure(t('pls_select_exit_no'));

                            return false;
                        }

                        break;
                    case 'two_phase':
                    case 'three_phase':
                        if (doubledOutputNumber?.value == null) {
                            failure(t('pls_select_exit_no'));

                            return false;
                        }

                        if (!isValidDuration(outputDurationLimitSeconds)) {
                            failure(t('pls_enter_a_valid_exit_duration_limit'));

                            return false;
                        }

                        break;
                    case 'pulse':
                        // no validations
                        break;
                    case 'complex':
                        if (controlType.value.id === CONTROL_TYPE_ID_DIMMER || controlType.value.id === CONTROL_TYPE_ID_ANALOG) {
                            if (outputNumber?.value == null) {
                                failure(t('pls_select_exit_no'));

                                return false;
                            }

                            if (!isValidMaxDimmerValue(maxValue)) {
                                failure(t('pls_enter_a_valid_maximum_value'));

                                return false;
                            }

                            if (!isValidMinDimmerValue(minValue)) {
                                failure(t('pls_enter_a_valid_minimum_value'));

                                return false;
                            }

                            if (Number(maxValue) <= Number(minValue)) {
                                failure(t('maximum_value_must_be_greater_then_minimum_vale'));

                                return false;
                            }
                        } else {
                            failure(t('undefined_control_type'));
                            console.log("13")

                            return false;
                        }

                        break;
                    default:
                        break;
                }
            }
        }

        return true;
    }, [
        t,
        failure,
        name,
        bridgeDevice,
        bridgeDeviceType,
        areaOption,
        isBridgeDeviceTypeIO,
        controlType,
        outputNumber?.value,
        rememberLastValue,
        defaultValueOption,
        buttonNumber?.value,
        outputDurationLimitSeconds,
        downOutputNumber?.value,
        doubledOutputNumber?.value,
        pulseDuration,
        maxValue,
        minValue,
        alarmLevelOption?.value,
        alarmControlOption?.value,
        alarmSirenOption?.value,
        alarmDelaySeconds,
        thermostatBridgeDevice?.value,
        heatingModeOption?.value,
        sirenLevelOption?.value,
        controlList,
        selected?.id,
    ]);

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        let ioOrGlassParameters: DeviceParameters;
        if (isBridgeDeviceTypeIO) {
            ioOrGlassParameters = {
                should_output_reverse: outputReverse,
                should_remember_last_value: rememberLastValue,
                default_value: defaultValueOption?.value ? defaultValueOption?.value : 0,
                output_number: outputNumber?.value,
            };
        } else {
            ioOrGlassParameters = {
                output_number: outputNumber?.value,
                button_number: buttonNumber?.value,
            };
        }

        let controlParameters: ControlParameters = null;
        let temperatureSettings: TemperatureControlSettings = null;
        switch (controlType.value.render_type) {
            case 'simple':
                controlParameters = {
                    should_output_reverse: outputReverse,
                };
                break;
            case 'two_phase':
            case 'three_phase':
                controlParameters = {
                    should_output_reverse: outputReverse,
                    down_output_number: isBridgeDeviceTypeIO ? downOutputNumber.value :
                        doubledOutputNumber.value === NO_OUTPUT_OPTION.value ? NO_OUTPUT_OPTION.value :
                            doubledOutputNumber.value + 1,
                    output_time_limit: Number(outputDurationLimitSeconds),
                };
                if (!isBridgeDeviceTypeIO) {
                    ioOrGlassParameters.output_number = doubledOutputNumber.value;
                }

                break;
            case 'pulse':
                controlParameters = {
                    pulse_time: Number(pulseDuration) * 10,
                };
                break;
            case 'complex':
                switch (controlType.value.id) {
                    case CONTROL_TYPE_ID_ANALOG:
                        controlParameters = {
                            min_limit: Number(minValue),
                            max_limit: Number(maxValue),
                        };
                        break;
                    case CONTROL_TYPE_ID_DIMMER:
                        controlParameters = {
                            min_limit: Number(minValue),
                            max_limit: Number(maxValue),
                        };
                        break;
                    case CONTROL_TYPE_ID_ALARM:
                        controlParameters = {
                            is_virtual_control: alarmControlOption.value.isVirtual,
                            relational_control_id: alarmControlOption.value.id,
                            siren_type: alarmSirenOption.value,
                            alarm_level: alarmLevelOption.value,
                            delay_time: Number(alarmDelaySeconds),
                        };
                        break;
                    case CONTROL_TYPE_ID_SIREN:
                        controlParameters = {
                            alarm_level: sirenLevelOption.value,
                        };
                        break;
                    case CONTROL_TYPE_ID_THERMOSTAT:
                        temperatureSettings = {
                            bridge_device_id: isBridgeDeviceTypeIO ? thermostatBridgeDevice.value.id : bridgeDevice.value.id,
                            is_mode_heating: heatingModeOption.value === 1 || heatingModeOption.value === 3,
                            has_heating: heatingModeOption.value === 1 || heatingModeOption.value === 3,
                            has_cooling: heatingModeOption.value === 2 || heatingModeOption.value === 3,
                        };

                        if (temperatureSettings.has_cooling && !(isBridgeDeviceTypeIO && isThermostatBridgeDeviceIO)) {
                            controlParameters = {
                                ...controlParameters,
                                down_output_number: downOutputNumber.value,
                            };
                            if (!temperatureSettings.has_heating) {
                                ioOrGlassParameters = {
                                    ...ioOrGlassParameters,
                                    output_number: downOutputNumber.value,
                                };
                            }
                        }

                        break;
                    default:
                        failure(t('undefined_control_type'));
                        console.log("12")

                        return;
                }

                break;
            default:
                break;
        }

        const controlModel: ControlModel = {
            ...(selected ? selected : {}),
            id: selected?.id || null,
            name,
            area_id: areaOption.value.id,
            bridge_device_id: bridgeDevice.value.id,
            type_id: controlType.value.id,
            parameters: {...ioOrGlassParameters, ...controlParameters},
            temperature_settings: temperatureSettings,
        };

        return rpc.updateControl(controlModel)
            .then((id: string) => {
                if (navigateToSTPEditor) {
                    goToStpEditorWithState({
                        controlId: id,
                        bridgeDeviceId: bridgeDevice.value.id,
                    });
                } else if (addInput) {
                    goToInputEditScreenWithState({
                        controlId: id,
                        bridgeDeviceId: bridgeDevice.value.id,
                        inputTypeId: controlType.value.id === CONTROL_TYPE_ID_ALARM ? INPUT_TYPE_ID_SENSOR : INPUT_TYPE_ID_MOMENTARY,
                    });
                } else {
                    success(t(selected?.id == null ? 'control_successfully_saved' : 'control_successfully_edited'));
                    onBackPressed();
                }
            })
            .catch(failure);
    }, [
        t,
        isFieldsValid,
        rpc,
        success,
        failure,
        onBackPressed,
        goToInputEditScreenWithState,
        addInput,
        selected,
        name,
        bridgeDevice,
        areaOption,
        isBridgeDeviceTypeIO,
        controlType,
        rememberLastValue,
        defaultValueOption,
        outputReverse,
        outputNumber?.value,
        buttonNumber?.value,
        downOutputNumber?.value,
        outputDurationLimitSeconds,
        doubledOutputNumber?.value,
        pulseDuration,
        maxValue,
        minValue,
        sirenLevelOption?.value,
        alarmLevelOption?.value,
        alarmSirenOption?.value,
        alarmDelaySeconds,
        alarmControlOption?.value,
        thermostatBridgeDevice?.value?.id,
        heatingModeOption?.value,
        navigateToSTPEditor,
        goToStpEditorWithState,
    ]);

    const setOutputReverseCheckbox = useCallback(() => {
        setOutputReverse(!outputReverse);
    }, [outputReverse]);

    const setRememberLastValueCheckbox = useCallback(() => {
        setRememberLastValue(rememberLastValue => !rememberLastValue);
    }, []);

    const setAddInputCheckbox = useCallback(() => {
        setAddInput(addInput => !addInput);
    }, []);

    const setNavigateStpEditorCheckbox = useCallback(() => {
        setNavigateToSTPEditor(!navigateToSTPEditor);
    }, [navigateToSTPEditor]);


    const buttons = useMemo(() => [
        {
            component: params?.id == null ? null :
                <SettingsDeleteButton
                    id={selected?.id}
                    type="control"
                    questionText={t('are_you_sure_you_want_to_delete_control')}
                    questionTextRelational={message => t('this_will_be_deleted_with_this_message__continue', {
                        name: selected?.name,
                        message,
                    })}
                    onPress={onDeletePressed}/>,
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onCreatePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        },
    ], [t, onCreatePressed, onDeletePressed, selected, params?.id]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
        setActiveNumpadExtendedInput(null);
    }, [setActiveInput, setActiveNumpadExtendedInput, setActiveNumpadInput]);

    useEffect(() => {
        setControlType(controlTypeOptionList.find(option => option.value === controlType?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlTypeOptionList]);

    useEffect(() => {
        setDefaultValueOption(defaultValueOptionList.find(option => option.value === defaultValueOption?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValueOptionList]);

    useEffect(() => {
        setOutputNumber(outputNumberOptionList.find(option => option.value === outputNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outputNumberOptionList]);

    useEffect(() => {
        setDownOutputNumber(outputNumberOptionList.find(option => option.value === downOutputNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outputNumberOptionList]);

    useEffect(() => {
        setDoubledOutputNumber(doubledOutputNumberOptionList.find(option => option.value === doubledOutputNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doubledOutputNumberOptionList]);

    useEffect(() => {
        setAlarmControlOption(alarmControlOptionList.find(option => option.value === alarmControlOption?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alarmControlOptionList]);

    useEffect(() => {
        if (thermostatBridgeDeviceType?.id == null) {
            return;
        }

        if (thermostatBridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_ST) {
            setHeatingModeOption(HEATING_MODE_LIST[0]);
        }
    }, [HEATING_MODE_LIST, thermostatBridgeDeviceType?.id]);

    useEffect(() => {
        if (!isBridgeDeviceTypeIO) {
            setThermostatBridgeDevice(thermostatBridgeDeviceOptionList.find(b => b.value.id === bridgeDevice.value.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bridgeDevice?.value?.id]);

    return (
        <PasswordContainer requiredRole="user" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={t('write_control_name')}
                            label={t('control_name')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t('device')}
                            options={bridgeDeviceOptionList}
                            onChange={setBridgeDevice}
                            value={bridgeDevice}/>



                        {
                            renderIf(bridgeDevice)(() =>
                                <>
                                    <SettingSelectComponent
                                        label={t('control_type')}
                                        options={controlTypeOptionList}
                                        onChange={setControlType}
                                        value={controlType}
                                    />

                                    {
                                        renderIf(isBridgeDeviceTypeIO && controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT)(() =>
                                            <>
                                                <SettingSelectComponent
                                                    label={t('sensor_device')}
                                                    options={thermostatBridgeDeviceOptionList}
                                                    onChange={setThermostatBridgeDevice}
                                                    value={thermostatBridgeDevice}/>
                                            </>,
                                        )
                                    }
                                    {
                                        renderIf(thermostatBridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_ST && thermostatBridgeDevice?.value && controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT)(() =>
                                            <SettingSelectComponent
                                                label={t('mode')}
                                                options={HEATING_MODE_LIST}
                                                onChange={setHeatingModeOption}
                                                value={heatingModeOption}/>,
                                        )
                                    }
                                    {
                                        renderIf(controlType?.value.id === CONTROL_TYPE_ID_ALARM)(() =>
                                            <>
                                                <SettingSelectComponent
                                                    label={t('control')}
                                                    options={alarmControlOptionList}
                                                    onChange={setAlarmControlOption}
                                                    value={alarmControlOption}/>
                                                <SettingSelectComponent
                                                    label={t('siren')}
                                                    options={SIREN_LIST}
                                                    onChange={setAlarmSirenOption}
                                                    value={alarmSirenOption}/>
                                                <SettingSelectComponent
                                                    label={t('alarm_level')}
                                                    options={ALARM_LEVEL_LIST}
                                                    onChange={setAlarmLevelOption}
                                                    value={alarmLevelOption}/>
                                                <SettingInputComponent
                                                    placeholder={t('alarm_filter_duration')}
                                                    label={t('filter_duration_sn')}
                                                    value={alarmDelaySeconds}
                                                    setValue={setAlarmDelaySeconds}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>, () =>
                                            renderIf(controlType?.value?.render_type === 'three_phase' || controlType?.value?.render_type === 'two_phase')(() =>
                                                [
                                                    renderIf(isBridgeDeviceTypeIO)(() =>
                                                        <Fragment key="output_no_io">
                                                            <SettingSelectComponent
                                                                label={t('output_no_up')}
                                                                options={outputNumberOptionList}
                                                                onChange={setOutputNumber}
                                                                value={outputNumber}/>
                                                            <SettingSelectComponent
                                                                label={t('output_no_down')}
                                                                options={outputNumberOptionList}
                                                                onChange={setDownOutputNumber}
                                                                value={downOutputNumber}/>
                                                        </Fragment>, () =>
                                                        <SettingSelectComponent
                                                            key="output_no_glass"
                                                            label={t('output_no')}
                                                            options={doubledOutputNumberOptionList}
                                                            onChange={setDoubledOutputNumber}
                                                            value={doubledOutputNumber}/>,
                                                    ),
                                                    <SettingInputComponent
                                                        key="output_duration"
                                                        placeholder={t('write_output_duration_limit')}
                                                        label={t('output_duration_limit')}
                                                        value={outputDurationLimitSeconds}
                                                        setValue={setOutputDurationLimitSeconds}
                                                        setActiveInput={setActiveNumpadInput}
                                                        onBlur={onBlur}/>,
                                                ], () =>
                                                renderIf(!(isBridgeDeviceTypeIO && isThermostatBridgeDeviceIO) && controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT)(() =>
                                                    renderIf(thermostatBridgeDevice?.value)(() =>
                                                        <>
                                                            {
                                                                renderIf(heatingModeOption.value === 1 || heatingModeOption.value === 3)(() =>
                                                                    <SettingSelectComponent
                                                                        label={t('output_no_heating')}
                                                                        options={outputNumberOptionList}
                                                                        onChange={setOutputNumber}
                                                                        value={outputNumber}/>,
                                                                )
                                                            }
                                                            {
                                                                renderIf(heatingModeOption.value === 2 || heatingModeOption.value === 3)(() =>
                                                                    <SettingSelectComponent
                                                                        label={t('output_no_cooling')}
                                                                        options={outputNumberOptionList}
                                                                        onChange={setDownOutputNumber}
                                                                        value={downOutputNumber}/>,
                                                                )
                                                            }
                                                        </>,
                                                    ), () =>
                                                    renderIf(controlType?.value)(() =>
                                                        <SettingSelectComponent
                                                            label={t('output_no')}
                                                            options={outputNumberOptionList}
                                                            onChange={setOutputNumber}
                                                            value={outputNumber}/>,
                                                    ),
                                                ),
                                            ),
                                        )
                                    }
                                    {
                                        renderIf(isBridgeDeviceTypeIO && controlType?.value?.render_type === 'pulse')(() =>
                                            <SettingInputComponent
                                                placeholder={t('write_pulse_duration')}
                                                label={t('pulse_duration')}
                                                value={pulseDuration}
                                                setValue={setPulseDuration}
                                                setActiveInput={setActiveNumpadExtendedInput}
                                                onBlur={onBlur}/>,
                                        )
                                    }
                                    {
                                        renderIf(controlType?.value.id === CONTROL_TYPE_ID_DIMMER|| controlType?.value.id === CONTROL_TYPE_ID_ANALOG)(() =>
                                            <>
                                                <SettingInputComponent
                                                    placeholder={t('write_maximum_value')}
                                                    label={t('maximum_value')}
                                                    value={maxValue}
                                                    setValue={setMaxValue}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder={t('write_minimum_value')}
                                                    label={t('minimum_value')}
                                                    value={minValue}
                                                    setValue={setMinValue}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>,
                                        )
                                    }
                                    {
                                        renderIf(controlType?.value.id === CONTROL_TYPE_ID_SIREN)(() =>
                                            <SettingSelectComponent
                                                label={t('alarm_level')}
                                                options={SIREN_LEVEL_LIST}
                                                onChange={setSirenLevelOption}
                                                value={sirenLevelOption}/>,
                                        )
                                    }
                                </>,
                            )
                        }
                    </div>
                    <div className="content-wrapper">
                        <SettingSelectComponent
                            label={t('area')}
                            options={areaOptionList}
                            onChange={setAreaOption}
                            value={areaOption}/>
                        {
                            renderIf(controlType != null && isBridgeDeviceTypeIO && bridgeDevice?.value?.type_id !== DeviceTypes.ETHERNET_IO)(() =>
                                <SettingCheckBoxComponent
                                    label={t('output_reverse')}
                                    set={setOutputReverseCheckbox}
                                    value={outputReverse}/>,
                            )
                        }
                        {
                            renderIf(controlType?.value != null &&
                                controlType.value.id !== CONTROL_TYPE_ID_ALARM &&
                                controlType.value.id !== CONTROL_TYPE_ID_SIREN)(() =>
                                renderIf(isBridgeDeviceTypeIO && controlType.value.id !== CONTROL_TYPE_ID_THERMOSTAT)(() =>
                                    <>
                                        <SettingCheckBoxComponent
                                            label={t('remember_last_status')}
                                            set={setRememberLastValueCheckbox}
                                            value={rememberLastValue}/>
                                        {
                                            renderIf(!rememberLastValue)(() =>
                                                <SettingSelectComponent
                                                    label={t('default_value')}
                                                    options={defaultValueOptionList}
                                                    onChange={setDefaultValueOption}
                                                    value={defaultValueOption}/>,
                                            )
                                        }
                                    </>, () =>
                                    <>
                                        {
                                            renderIf(bridgeDeviceType?.button_count > 0)(() =>
                                                <SettingSelectComponent
                                                    label={t('button_number')}
                                                    options={buttonNumberOptionList}
                                                    onChange={setButtonNumber}
                                                    value={buttonNumber}/>,
                                            )
                                        }
                                    </>,
                                ))
                        }
                        {
                            renderIf(!isBridgeDeviceTypeIO && controlType?.value?.render_type === 'simple')(() =>
                                <SettingCheckBoxComponent
                                    label={t('output_reverse')}
                                    set={setOutputReverseCheckbox}
                                    value={outputReverse}/>,
                            )
                        }
                        {
                            renderIf(selected?.id == null &&
                                controlType?.value != null &&
                                controlType.value.id !== CONTROL_TYPE_ID_THERMOSTAT &&
                                isBridgeDeviceTypeIO && bridgeDevice?.value?.type_id !== DeviceTypes.ETHERNET_IO)(() =>
                                <SettingCheckBoxComponent
                                    label={t('add_input')}
                                    set={setAddInputCheckbox}
                                    value={addInput}/>,
                            )
                        }
                        {
                            renderIf(selected?.id == null &&
                                (bridgeDeviceType?.page_count > 0
                                    && bridgeDeviceType?.id !== '6'
                                    /*||
                                    (isBridgeDeviceTypeIO && controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT && !isThermostatBridgeDeviceIO)*/))(() =>
                                <SettingCheckBoxComponent
                                    label={t(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_STS_PLUS ? 'go_to_sts_plus_editor' : 'go_to_stp_editor')}
                                    set={setNavigateStpEditorCheckbox}
                                    value={navigateToSTPEditor}/>,
                            )
                        }
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
            {
                renderNumpadExtendedKeyboard()
            }
        </PasswordContainer>
    );
});
